import React from 'react';
import clsx, { ClassValue } from 'clsx';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'common/i18n/hooks';
import { InsightsLineChartReact } from 'common/report/insights/charts/insights-line-chart';
import { useExperimentsStore } from 'apps/experiments/src/store';
import { getLatestOverallSeriesValue, getLatestSeriesValues } from 'apps/experiments/src/components/query-utils';
import { ReactErrorBoundary } from 'common/components/react-error-boundary';
import { PageTrackingEvent, trackExperimentPageAction } from 'apps/experiments/src/components/tracking';
import { useExperimentPageContext } from 'iron/apps/experiments/src/components/context';
import { useStagedExperiment } from 'iron/apps/experiments/src/components/hooks/staged-experiment';
import { SvgIconReact } from 'common/components/svg-icon';
import { RESULTS_OVERALL_SUM_KEY } from 'common/report/constants';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize } from 'common/components/spinner/types';
import HealthEmptySrc from 'common/assets/images/experiments/health-empty.svg';
import { useExposuresSeriesName, useVariants } from 'iron/apps/experiments/src/components/hooks/metric-results';
import { DEFAULT_CONTROL_KEY } from 'iron/apps/experiments/src/components/utils';
import { ExperimentPageSummaryCard } from './card';
import styles from './_exposures-card.module.styl';
type ExperimentPageSummaryCardProps = {
  className?: ClassValue;
};
export const ExposuresSummaryCard: React.FC<ExperimentPageSummaryCardProps> = props => {
  const {
    stagedExperiment: experiment
  } = useStagedExperiment();
  const exposuresQueryResults = useExperimentsStore(state => state.exposuresQueryResults);
  const i18n = useTranslation();
  const firedLoadEvent = React.useRef(false);
  if (!experiment) {
    return null;
  }
  React.useEffect(() => {
    if (exposuresQueryResults && !firedLoadEvent.current) {
      firedLoadEvent.current = true;
      trackExperimentPageAction({
        event: PageTrackingEvent.ExposuresLoaded,
        experiment,
        props: {
          'experiment - exposures': getLatestOverallSeriesValue(exposuresQueryResults)
        }
      });
    }
  }, [exposuresQueryResults]);
  return <ExperimentPageSummaryCard className={clsx(props.className)} header={i18n.t("experiments.main.summary.health")} data-sentry-element="ExperimentPageSummaryCard" data-sentry-component="ExposuresSummaryCard" data-sentry-source-file="_exposures-card.tsx">
      <div className={styles.body}>
        <ContentResults data-sentry-element="ContentResults" data-sentry-source-file="_exposures-card.tsx" />
      </div>
    </ExperimentPageSummaryCard>;
};
function ContentResults() {
  var _useExposuresSeriesNa, _experiment$settings$, _variantsResults$RESU;
  const i18n = useTranslation();
  const {
    stagedExperiment: experiment
  } = useStagedExperiment();
  const {
    segmentFilterController
  } = useExperimentPageContext();
  const exposuresBookmarkParams = useExperimentsStore(state => state.exposuresBookmarkParams);
  const exposuresQueryResults = useExperimentsStore(state => state.exposuresQueryResults);
  const exposuresSeriesName = (_useExposuresSeriesNa = useExposuresSeriesName()) !== null && _useExposuresSeriesNa !== void 0 ? _useExposuresSeriesNa : "";
  const controlKey = (_experiment$settings$ = experiment === null || experiment === void 0 ? void 0 : experiment.settings.controlKey) !== null && _experiment$settings$ !== void 0 ? _experiment$settings$ : DEFAULT_CONTROL_KEY;
  const variants = useVariants();
  const [segmentFilterControllerCounter, setSegmentFilterControllerCounter] = React.useState(0);
  const incrementSegmentFilterControllerCounter = React.useCallback(() => {
    setSegmentFilterControllerCounter(prev => prev + 1);
  }, []);
  React.useEffect(() => {
    segmentFilterController === null || segmentFilterController === void 0 || segmentFilterController.subscribe(incrementSegmentFilterControllerCounter);
    return () => {
      segmentFilterController === null || segmentFilterController === void 0 || segmentFilterController.unsubscribe(incrementSegmentFilterControllerCounter);
    };
  }, [segmentFilterController, incrementSegmentFilterControllerCounter]);
  const percentFormatter = Intl.NumberFormat(i18n.language, {
    style: "percent",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
  });
  const variantsResults = React.useMemo(() => exposuresQueryResults ? getLatestSeriesValues(exposuresQueryResults) : {}, [exposuresQueryResults]);
  const overallResults = (_variantsResults$RESU = variantsResults === null || variantsResults === void 0 ? void 0 : variantsResults[RESULTS_OVERALL_SUM_KEY]) !== null && _variantsResults$RESU !== void 0 ? _variantsResults$RESU : 0;
  const noResults = isEmpty(exposuresQueryResults === null || exposuresQueryResults === void 0 ? void 0 : exposuresQueryResults.series) || overallResults === 0;
  const legendItems: LegendItemProps[] = React.useMemo(() => {
    if (noResults) {
      return [];
    }
    return [controlKey, ...Array.from(variants)].map(variant => {
      var _variantsResults$vari;
      const variantKey = "".concat(exposuresSeriesName, ",").concat(variant);
      const color = segmentFilterController === null || segmentFilterController === void 0 ? void 0 : segmentFilterController.getItemColor(0, variantKey);
      const percent = percentFormatter.format(((_variantsResults$vari = variantsResults === null || variantsResults === void 0 ? void 0 : variantsResults[variant]) !== null && _variantsResults$vari !== void 0 ? _variantsResults$vari : 0) / overallResults);
      return {
        label: variant,
        color: color !== null && color !== void 0 ? color : "#000",
        percent
      };
    });
  }, [controlKey, noResults, exposuresSeriesName, segmentFilterControllerCounter, variants, variantsResults, overallResults]);
  if (exposuresQueryResults) {
    if (noResults) {
      return <NoResults />;
    }
    return <>
        <LegendItemList items={legendItems} />
        <div className={styles.chart}>
          {exposuresBookmarkParams && <ReactErrorBoundary>
              <InsightsLineChartReact loaded={!!exposuresQueryResults} emptyDataContainerClassName={styles.emptyData} paramsAndResults={{
            results: exposuresQueryResults!,
            params: exposuresBookmarkParams
          }} segmentFilterController={segmentFilterController!} hideLegend primaryYAxisOptions={{
            visible: false
          }} allowTooltipOutsideChart />
            </ReactErrorBoundary>}
        </div>
      </>;
  } else if (!exposuresBookmarkParams) {
    return <NoResults />;
  }
  return <LoadingResults data-sentry-element="LoadingResults" data-sentry-component="ContentResults" data-sentry-source-file="_exposures-card.tsx" />;
}
type LegendItemProps = {
  label: string;
  color: string;
  percent: string;
};
function LegendItemList(props: {
  items: LegendItemProps[];
}) {
  return <div className={styles.legend} data-sentry-component="LegendItemList" data-sentry-source-file="_exposures-card.tsx">
      {props.items.map(item => <LegendItem key={item.label} label={item.label} color={item.color} percent={item.percent} />)}
    </div>;
}
function LegendItem(_ref: {
  label: string;
  color: string;
  percent: string;
}) {
  let {
    label,
    color,
    percent
  }: {
    label: string;
    color: string;
    percent: string;
  } = _ref;
  return <div className={styles.legendItem} data-sentry-component="LegendItem" data-sentry-source-file="_exposures-card.tsx">
      <SvgIconReact icon='chart-color' color={color} data-sentry-element="SvgIconReact" data-sentry-source-file="_exposures-card.tsx" />
      <span className={styles.legendItemLabel} title={label}>
        {label}
      </span>
      <span className={styles.legendItemLabelSeparator}>:</span>
      <span className={styles.legendItemPercent}>{percent}</span>
    </div>;
}
function NoResults() {
  const i18n = useTranslation();
  return <div className={styles.noResults} data-sentry-component="NoResults" data-sentry-source-file="_exposures-card.tsx">
      <div className={styles.noResultsInner}>
        <LegendItemList items={[{
        label: "Control",
        color: "#7856FF",
        percent: "49.7%"
      }, {
        label: "Variant",
        color: "#FF7557",
        percent: "50.3%"
      }]} data-sentry-element="LegendItemList" data-sentry-source-file="_exposures-card.tsx" />
        <div className={styles.chart}>
          <img className={styles.noResultsImage} src={HealthEmptySrc} alt='No results' />
        </div>
      </div>
      <div className={styles.noResultsMessage}>
        <SvgIconReact className={styles.noResultsIcon} icon='circle' color='var(--gray3)' data-sentry-element="SvgIconReact" data-sentry-source-file="_exposures-card.tsx" />
        {i18n.t("experiments.main.summary.health-no-results")}
      </div>
    </div>;
}
function LoadingResults() {
  return <div className={styles.loading} data-sentry-component="LoadingResults" data-sentry-source-file="_exposures-card.tsx">
      <SpinnerReact size={SpinnerSize.Large} data-sentry-element="SpinnerReact" data-sentry-source-file="_exposures-card.tsx" />
    </div>;
}